import React from 'react';
import { lookupChain } from '../data/siteContent';

/**
 * Ceremonial band. The offline-first resolution chain is the app's strongest
 * technical claim, so it gets the page's darkest, quietest block.
 */
const LookupSection: React.FC = () => {
  return (
    <section id="lookup" className="band-night section-y">
      <div className="shell">
        <div className="max-w-2xl">
          <p className="kicker text-primary-lift">{lookupChain.eyebrow}</p>
          <h2 className="display-lg mt-5 text-[2.25rem] text-white sm:text-[3rem]">
            {lookupChain.title}
          </h2>
          <p className="prose-reading mt-6 text-white/70">{lookupChain.body}</p>
        </div>

        {/* The specimen: one word, resolved. */}
        <div className="mt-14 grid gap-gutter lg:grid-cols-12">
          <figure className="self-start border border-white/15 bg-night-soft/40 p-7 shadow-plate-night sm:p-9 lg:col-span-5">
            <p className="kicker text-white/60">Tapped word</p>
            <p
              className="mt-3 font-display text-[2.75rem] leading-none text-white sm:text-[3.25rem]"
              lang="it"
            >
              {lookupChain.word}
            </p>
            <p className="mt-2 font-heading text-base italic text-white/60">{lookupChain.ipa}</p>

            <div className="mt-7 flex items-baseline gap-3 border-t border-white/15 pt-6">
              <span className="text-primary-lift" aria-hidden="true">
                ↳
              </span>
              <div>
                <p className="kicker text-white/60">Resolves to</p>
                <p className="mt-1.5 font-display text-2xl text-primary-lift" lang="it">
                  {lookupChain.lemma}
                </p>
                <p className="prose-note mt-1 text-white/75">{lookupChain.gloss}</p>
              </div>
            </div>

            <blockquote className="mt-7 border-l-2 border-white/25 pl-4 font-heading text-[0.9375rem] italic leading-relaxed text-white/55">
              <p lang="it">{lookupChain.sentence}</p>
            </blockquote>
          </figure>

          {/* The cascade. */}
          <ol className="list-none lg:col-span-7">
            {lookupChain.steps.map((step) => (
              <li
                key={step.step}
                className="grid grid-cols-[3rem_1fr] gap-5 border-t border-white/15 py-7 first:border-t-0 first:pt-0 sm:grid-cols-[4rem_1fr] sm:gap-7"
              >
                <span
                  className={`font-display text-2xl leading-none sm:text-3xl ${
                    step.offline ? 'text-primary-lift' : 'text-white/45'
                  }`}
                  aria-hidden="true"
                >
                  {step.step}
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="display-sm text-xl text-white">{step.title}</h3>
                    <span
                      className={`border px-2 py-0.5 font-body text-[0.625rem] font-semibold uppercase tracking-small-caps ${
                        step.offline
                          ? 'border-primary-lift/50 text-primary-lift'
                          : 'border-white/25 text-white/60'
                      }`}
                    >
                      {step.offline ? 'Offline' : 'Needs network'}
                    </span>
                  </div>
                  <p className="prose-note mt-2.5 text-white/65">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default LookupSection;
